import { lazy, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import type { TeamType } from '@modules/Auth/models/Auth';
/** Eager: layout de tabs; no debe suspender al cambiar Activos ↔ Informes. */
import { MerchandiseBlocksShell } from '@modules/MerchandiseBlocks/MerchandiseBlocksShell';
import { APP_CONFIG } from '@config';
import { lazyNamed } from './lazyNamed';

// import { Merchandise } from '@modules/Merchandise'; // OCULTO - usar MerchandiseV3

/**
 * Pantallas de las rutas protegidas, cargadas bajo demanda y agrupadas por
 * módulo de origen.
 *
 * Antes de esto el archivo declaraba 137 imports estáticos sobre 102
 * destinos: abrir cualquier pantalla obligaba a transformar y servir el grafo de
 * todas, que es lo que hacía lento el arranque y el HMR en desarrollo. Varias
 * pantallas que comparten especificador comparten chunk.
 *
 * Las entradas del array de rutas no cambian: sólo cambia **cómo** se carga el
 * componente, nunca quién puede llegar a él.
 */
const DataLoad = lazyNamed(() => import('@modules/Accounting/DataLoad'), 'DataLoad');
const AccountingDashboard = lazyNamed(() => import('@modules/Accounting/Dashboard'), 'AccountingDashboard');
const AnalyticalAccounting = lazyNamed(() => import('@modules/AnalyticalAccounting'), 'AnalyticalAccounting');
const BatchDetail = lazyNamed(() => import('@modules/Batches/Detail'), 'BatchDetail');
const Batches = lazyNamed(() => import('@modules/Batches/List'), 'Batches');
const CourierFilesList = lazyNamed(() => import('@modules/CODPayments'), 'CourierFilesList');
const CourierFileView = lazyNamed(() => import('@modules/CODPayments'), 'CourierFileView');
const MismatchesList = lazyNamed(() => import('@modules/CODPayments'), 'MismatchesList');
const PayoutItemsList = lazyNamed(() => import('@modules/CODPayments'), 'PayoutItemsList');
const PayoutsList = lazyNamed(() => import('@modules/CODPayments'), 'PayoutsList');
const PayoutView = lazyNamed(() => import('@modules/CODPayments'), 'PayoutView');
const ReviewsList = lazyNamed(() => import('@modules/CODPayments'), 'ReviewsList');
const ViewCRM = lazyNamed(() => import('@modules/CRM/Details'), 'ViewCRM');
const CRM = lazyNamed(() => import('@modules/CRM/List'), 'CRM');
const ManagementHours = lazyNamed(() => import('@modules/CRM/ManagementHours'), 'ManagementHours');
const Carts = lazyNamed(() => import('@modules/Carts'), 'Carts');
const ClientPackagingRecipes = lazyNamed(() => import('@modules/Clients'), 'ClientPackagingRecipes');
const ClientRules = lazyNamed(() => import('@modules/Clients'), 'ClientRules');
const Clients = lazyNamed(() => import('@modules/Clients'), 'Clients');
const CreateEditClient = lazyNamed(() => import('@modules/Clients'), 'CreateEditClient');
const ViewClient = lazyNamed(() => import('@modules/Clients'), 'ViewClient');
const ClientRulesRedirectFromCreate = lazyNamed(
  () => import('@modules/Clients/ClientRules/ClientRulesLegacyRedirects'),
  'ClientRulesRedirectFromCreate',
);
const ClientRulesRedirectFromEdit = lazyNamed(
  () => import('@modules/Clients/ClientRules/ClientRulesLegacyRedirects'),
  'ClientRulesRedirectFromEdit',
);
const ClientRulesRedirectFromView = lazyNamed(
  () => import('@modules/Clients/ClientRules/ClientRulesLegacyRedirects'),
  'ClientRulesRedirectFromView',
);
const CustomerSupportHub = lazyNamed(() => import('@modules/CustomerSupport'), 'CustomerSupportHub');
const TicketDetailView = lazyNamed(() => import('@modules/CustomerSupport'), 'TicketDetailView');
const ExtraWorkOrderDetail = lazyNamed(() => import('@modules/ExtraWorkOrders'), 'ExtraWorkOrderDetail');
const ExtraWorkOrdersList = lazyNamed(() => import('@modules/ExtraWorkOrders'), 'ExtraWorkOrdersList');
const Home = lazyNamed(() => import('@modules/Home'), 'Home');
const EligibleWholesalePreparation = lazyNamed(
  () => import('@modules/Invoices/EligibleWholesalePreparation'),
  'EligibleWholesalePreparation',
);
const EligibleCrmManagement = lazyNamed(
  () => import('@modules/Invoices/EligibleCrmManagement'),
  'EligibleCrmManagement',
);
const EligibleExtraWork = lazyNamed(() => import('@modules/Invoices/EligibleExtraWork'), 'EligibleExtraWork');
const EligibleStorage = lazyNamed(() => import('@modules/Invoices/EligibleStorage'), 'EligibleStorage');
const InvoicesList = lazyNamed(() => import('@modules/Invoices/Invoices/List'), 'Invoices');
const InvoicesView = lazyNamed(() => import('@modules/Invoices/Invoices/View'), 'InvoicesView');
const InvoicesOrdersList = lazyNamed(() => import('@modules/Invoices/InvoicesOrders/List'), 'InvoicesOrders');
const MinimumFees = lazyNamed(() => import('@modules/Invoices/MinimumFees'), 'MinimumFees');
const PreInvoices = lazyNamed(() => import('@modules/Invoices/PreInvoices/List'), 'PreInvoices');
const ViewPreInvoices = lazyNamed(() => import('@modules/Invoices/PreInvoices/View'), 'ViewPreInvoices');
const ShipmentCredits = lazyNamed(() => import('@modules/Invoices/ShipmentCredits'), 'ShipmentCredits');
const Detail = lazyNamed(() => import('@modules/LocationOrders/Detail'), 'Detail');
const LocationOrders = lazyNamed(() => import('@modules/LocationOrders/List'), 'LocationOrders');
const Locations = lazyNamed(() => import('@modules/Locations/Locations'), 'Locations');
const MerchandiseBlocksList = lazyNamed(() => import('@modules/MerchandiseBlocks/List'), 'MerchandiseBlocksList');
const MerchandiseBlocksHistory = lazyNamed(
  () => import('@modules/MerchandiseBlocks/History'),
  'MerchandiseBlocksHistory',
);
const MerchandiseBlockDetail = lazyNamed(() => import('@modules/MerchandiseBlocks/Detail'), 'MerchandiseBlockDetail');
const SlaAlerts = lazyNamed(() => import('@modules/Logistics'), 'SlaAlerts');
const SlaClaims = lazyNamed(() => import('@modules/Logistics'), 'SlaClaims');
const SlaRules = lazyNamed(() => import('@modules/Logistics'), 'SlaRules');
const SlaFalseDelivery = lazyNamed(() => import('@modules/Logistics'), 'SlaFalseDelivery');
const SlaBreachTypes = lazyNamed(() => import('@modules/Logistics'), 'SlaBreachTypes');
const TrackingFailures = lazyNamed(() => import('@modules/Logistics'), 'TrackingFailures');
const MerchandiseV3 = lazyNamed(() => import('@modules/MerchandiseV3'), 'MerchandiseV3');
const Incidents = lazyNamed(() => import('@modules/OrderIncidents/Incidents/List'), 'Incidents');
const Mappings = lazyNamed(() => import('@modules/OrderIncidents/Mappings/List'), 'Mappings');
const OrderPreparation = lazyNamed(() => import('@modules/OrderPreparation/List'), 'OrderPreparation');
const Manifests = lazyNamed(() => import('@modules/OrderPreparation/Manifests'), 'Manifests');
const MassivePacking = lazyNamed(() => import('@modules/OrderPreparation/MassivePacking'), 'MassivePacking');
const PackingByCart = lazyNamed(() => import('@modules/OrderPreparation/PackingByCart'), 'PackingByCart');
const PackingV2 = lazyNamed(() => import('@modules/OrderPreparation/PackingV2'), 'PackingV2');
const Picking = lazyNamed(() => import('@modules/OrderPreparation/Picking'), 'Picking');
const CreateEditOrders = lazyNamed(() => import('@modules/Orders/CreateEditOrders'), 'CreateEditOrders');
const Orders = lazyNamed(() => import('@modules/Orders/List'), 'Orders');
const ShipmentStatusTransitions = lazyNamed(
  () => import('@modules/Orders/ShipmentStatusTransitions'),
  'ShipmentStatusTransitions',
);
const ViewOrders = lazyNamed(() => import('@modules/Orders/ViewOrders'), 'ViewOrders');
const OrdersShipped = lazyNamed(() => import('@modules/OrdersShipped'), 'OrdersShipped');
const Packaging = lazyNamed(() => import('@modules/Packaging'), 'Packaging');
const PackingStations = lazyNamed(() => import('@modules/Packaging/PackingStations/List'), 'PackingStations');
const Packs = lazyNamed(() => import('@modules/Packs'), 'Packs');
const PendingOrders = lazy(() => import('@modules/PendingOrders/PendingOrders'));
const OrdersHeldForPreparation = lazyNamed(
  () => import('@modules/OrdersHeldForPreparation/Manage'),
  'OrdersHeldForPreparation',
);
const Popups = lazyNamed(() => import('@modules/Popups'), 'Popups');
const ProductCreateEdit = lazyNamed(() => import('@modules/Products/CreateEdit'), 'CreateEdit');
const Products = lazyNamed(() => import('@modules/Products/List'), 'Products');
const CreateRate = lazyNamed(() => import('@modules/Rates'), 'CreateRate');
const RateDetail = lazyNamed(() => import('@modules/Rates'), 'RateDetail');
const RatesList = lazyNamed(() => import('@modules/Rates'), 'RatesList');
const ReceptionDetailPage = lazyNamed(() => import('@modules/Receptions'), 'ReceptionDetailPage');
const Receptions = lazyNamed(() => import('@modules/Receptions'), 'Receptions');
const StockTransferEditor = lazyNamed(() => import('@modules/StockTransfers'), 'StockTransferEditor');
const CrossDockingOperationsList = lazyNamed(() => import('@modules/CrossDocking'), 'CrossDockingOperationsList');
const CrossDockingOperationsCreate = lazyNamed(() => import('@modules/CrossDocking'), 'CrossDockingOperationsCreate');
const CrossDockingOperationsDetail = lazyNamed(() => import('@modules/CrossDocking'), 'CrossDockingOperationsDetail');
const CrossDockingReceptionWorkspace = lazyNamed(
  () => import('@modules/CrossDocking'),
  'CrossDockingReceptionWorkspace',
);
const CrossDockingReceiving = lazyNamed(() => import('@modules/CrossDocking'), 'CrossDockingReceiving');
const CrossDockingPacking = lazyNamed(() => import('@modules/CrossDocking'), 'CrossDockingPacking');
const Relocations = lazyNamed(() => import('@modules/Relocations/List'), 'Relocations');
const RelocationProcess = lazyNamed(() => import('@modules/Relocations/Process'), 'RelocationProcess');
const Inventory = lazyNamed(() => import('@modules/Reports/Inventory'), 'Inventory');
const LegacyDashboard = lazyNamed(() => import('@modules/Reports/LegacyDashboard'), 'LegacyDashboard');
const ClientActivityPage = lazyNamed(() => import('@modules/Reports/ClientActivity'), 'ClientActivityPage');
const ClientActivityOrdersPage = lazyNamed(() => import('@modules/Reports/ClientActivity'), 'ClientActivityOrdersPage');
const LocationMovementsV2 = lazyNamed(() => import('@modules/Reports/LocationMovements'), 'LocationMovements');
const Logistics = lazyNamed(() => import('@modules/Reports/Logistics'), 'Logistics');
const Marketing = lazyNamed(() => import('@modules/Reports/Marketing'), 'Marketing');
const OrdersWithoutWeight = lazy(() => import('@modules/Reports/OrdersWithoutWeight'));
const ProductsWithoutStock = lazyNamed(() => import('@modules/Reports/ProductsWithoutStock'), 'ProductsWithoutStock');
const AdSpendPage = lazyNamed(() => import('@modules/Reports/Profitability'), 'AdSpendPage');
const DefaultShippingCostsPage = lazyNamed(() => import('@modules/Reports/Profitability'), 'DefaultShippingCostsPage');
const ProductCostsPage = lazyNamed(() => import('@modules/Reports/Profitability'), 'ProductCostsPage');
const Profitability = lazyNamed(() => import('@modules/Reports/Profitability'), 'Profitability');
const ProfitabilityOrderDetailPage = lazyNamed(
  () => import('@modules/Reports/Profitability'),
  'ProfitabilityOrderDetailPage',
);
const ReadyToPrepareByHour = lazyNamed(() => import('@modules/Reports/ReadyToPrepareByHour'), 'ReadyToPrepareByHour');
const Refunds = lazyNamed(() => import('@modules/Reports/Refunds'), 'Refunds');
const RefundsList = lazyNamed(() => import('@modules/Refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('@modules/Refunds'), 'RefundDetail');
const RefundReceiveEntry = lazyNamed(() => import('@modules/RefundReceptions'), 'RefundReceiveEntry');
const RefundReceptionsList = lazyNamed(() => import('@modules/RefundReceptions'), 'RefundReceptionsList');
const RefundReceptionsReceive = lazyNamed(() => import('@modules/RefundReceptions'), 'RefundReceptionsReceive');
const StockMovementsV2 = lazyNamed(() => import('@modules/Reports/StockMovements'), 'StockMovements');
const WarehouseStockMovements = lazyNamed(() => import('@modules/Reports/StockMovements'), 'WarehouseStockMovements');
const ThreePlExcelReports = lazyNamed(() => import('@modules/Reports/ThreePlExcelReports'), 'ThreePlExcelReports');
const WarehouseExcelReports = lazyNamed(
  () => import('@modules/Reports/WarehouseExcelReports'),
  'WarehouseExcelReports',
);
const Tracking = lazyNamed(() => import('@modules/Reports/Tracking'), 'Tracking');
const Returned = lazyNamed(() => import('@modules/Returned'), 'Returned');
const Locate = lazyNamed(() => import('@modules/Returned/Receive'), 'Locate');
const Receive = lazyNamed(() => import('@modules/Returned/Receive'), 'Receive');
const ReusedList = lazyNamed(() => import('@modules/Reused'), 'ReusedList');
const ProductivityPage = lazyNamed(() => import('@modules/Productivity'), 'ProductivityPage');
const ReusedDashboard = lazyNamed(() => import('@modules/Reused'), 'ReusedDashboard');
const RedirectLegacyReusedDashboard = lazyNamed(() => import('@modules/Reused'), 'RedirectLegacyReusedDashboard');
const ReusedReceive = lazyNamed(() => import('@modules/Reused'), 'ReusedReceive');
const ReusedIdentifyApp = lazyNamed(() => import('@modules/Reused'), 'ReusedIdentifyApp');
const ImportHistory = lazy(() => import('@modules/Shops/ImportHistory/ImportHistory'));
const Shops = lazyNamed(() => import('@modules/Shops/List'), 'Shops');
const MercadoLibreCallback = lazyNamed(
  () => import('@modules/Shops/MercadoLibreCallback/MercadoLibreCallback'),
  'MercadoLibreCallback',
);
const UnreferencedEntryDetail = lazyNamed(
  () => import('@modules/UnreferencedEntries/Detail'),
  'UnreferencedEntryDetail',
);
const UnreferencedEntries = lazyNamed(() => import('@modules/UnreferencedEntries/List'), 'UnreferencedEntries');
const AccountingSyncList = lazyNamed(() => import('@modules/Wallets/AccountingSync'), 'AccountingSyncList');
const BankAccounts = lazyNamed(() => import('@modules/Wallets/BankAccounts'), 'BankAccounts');
const Wallets = lazyNamed(() => import('@modules/Wallets/List'), 'Wallets');
const Topups = lazyNamed(() => import('@modules/Wallets/Topups'), 'Topups');
const Wallet = lazyNamed(() => import('@modules/Wallets/Wallet'), 'Wallet');
const WithdrawalPaymentFiles = lazyNamed(
  () => import('@modules/Wallets/WithdrawalPaymentFiles'),
  'WithdrawalPaymentFiles',
);
const Withdrawals = lazyNamed(() => import('@modules/Wallets/Withdrawals'), 'Withdrawals');
const WarehouseMapMain = lazyNamed(() => import('@modules/WarehouseMap/components/containers/Main'), 'Main');
const WarehouseMapView = lazyNamed(() => import('@modules/WarehouseMap/components/display/index'), 'WarehouseMapView');
// Hotfix: afiliados ocultos. Para reactivar, descomentar estos imports, sus
// rutas y sus entradas de menuConfig, restaurar la línea base de rutas y
// eliminar test/shared/components/navigation/affiliatesHidden.test.ts.
// const Affiliates = lazy(() => import('@modules/configuration/Affiliates/Affiliates'));
// const AffiliatesListPage = lazyNamed(() => import('@modules/AffiliatePartners'), 'AffiliatesListPage');
// const AffiliateDetailPage = lazyNamed(() => import('@modules/AffiliatePartners'), 'AffiliateDetailPage');
// const AffiliateCommissionsListPage = lazyNamed(
//   () => import('@modules/AffiliateCommissions'),
//   'AffiliateCommissionsListPage',
// );
const CoverageZones = lazy(() => import('@modules/configuration/CoverageZones'));
const General = lazyNamed(() => import('@modules/configuration/General'), 'General');
const IntegratedMessaging = lazyNamed(
  () => import('@modules/configuration/IntegratedMessaging'),
  'IntegratedMessaging',
);
const Integrations = lazyNamed(() => import('@modules/configuration/Integrations'), 'Integrations');
const NotificationSettings = lazyNamed(
  () => import('@modules/configuration/NotificationSettings'),
  'NotificationSettings',
);
const WholesalePreparations = lazyNamed(() => import('@modules/WholesaleOrders/Preparations'), 'PreparationsPage');
const WholesaleList = lazyNamed(() => import('@modules/WholesaleOrders/List'), 'WholesaleOrdersPage');
const WholesaleCreate = lazyNamed(() => import('@modules/WholesaleOrders/Create'), 'CreateWholesaleOrderPage');
const WholesaleDetail = lazyNamed(() => import('@modules/WholesaleOrders/Detail'), 'WholesaleOrderDetailPage');
const WholesalePreparation = lazyNamed(() => import('@modules/WholesaleOrders/Preparation'), 'PreparationPage');
const WholesalePickups = lazyNamed(() => import('@modules/WholesaleOrders/Pickups'), 'PickupsPage');
const WholesaleLabels = lazyNamed(() => import('@modules/WholesaleOrders/Labels'), 'LabelsPage');
const WholesaleStagingZones = lazyNamed(() => import('@modules/WholesaleOrders/Configuration'), 'Configuration');
const Printers = lazyNamed(() => import('@modules/configuration/Printers'), 'Printers');
const PreparationCalendar = lazyNamed(
  () => import('@modules/configuration/PreparationCalendar/Manage'),
  'PreparationCalendar',
);
const ProductCategories = lazyNamed(() => import('@modules/configuration/ProductCategories'), 'ProductCategories');
const Profile = lazyNamed(() => import('@modules/configuration/Profile'), 'Profile');
const Roles = lazyNamed(() => import('@modules/configuration/Roles'), 'Roles');
const ShipmentStatusMapping = lazyNamed(
  () => import('@modules/configuration/ShipmentStatusMapping/ShipmentStatusMapping'),
  'ShipmentStatusMapping',
);
const ShippingRulePresetEditor = lazyNamed(
  () => import('@modules/configuration/ShippingRulePresets'),
  'ShippingRulePresetEditor',
);
const ShippingRulePresets = lazyNamed(
  () => import('@modules/configuration/ShippingRulePresets'),
  'ShippingRulePresets',
);
const ShippingRulePresetsHistory = lazyNamed(
  () => import('@modules/configuration/ShippingRulePresets'),
  'ShippingRulePresetsHistory',
);
const Suscription = lazyNamed(() => import('@modules/configuration/Suscription'), 'Suscription');
const TrackingFiles = lazyNamed(() => import('@modules/configuration/TrackingFiles'), 'TrackingFiles');
const Users = lazy(() => import('@modules/configuration/Users/Users'));
const VerifyNewEmailPage = lazy(() => import('@modules/configuration/Users/pages/VerifyNewEmailPage'));
const WarehouseMaterials = lazyNamed(() => import('@modules/configuration/WarehouseMaterials'), 'WarehouseMaterials');
const WebhookDeliveriesView = lazyNamed(() => import('@modules/configuration/Webhooks'), 'WebhookDeliveriesView');
const Webhooks = lazyNamed(() => import('@modules/configuration/Webhooks'), 'Webhooks');

export interface ProtectedRouteConfig {
  path: string;
  element: ReactNode;
  /** Rutas hijas (layout con `<Outlet />`). El índice usa `index: true`. */
  children?: ProtectedRouteChildConfig[];
  teamTypes?: TeamType[];
  permissions?: string[];
  requireAll?: boolean;
  roleAdminAlternativeAccess?: { teamTypes: TeamType[]; permissions: string[] };
  requireRoleAdmin?: boolean;
  requireCRMEnabled?: boolean; // Si true, solo permitir acceso si crm.enabled es true
  requireWalletVisible?: boolean;
  requireProfitabilityAccess?: boolean;
}

export interface ProtectedRouteChildConfig {
  path?: string;
  index?: boolean;
  element: ReactNode;
}

export const protectedRoutes: ProtectedRouteConfig[] = [
  {
    path: '',
    element: <Navigate to="/home" replace />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'home',
    element: <Home />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'products',
    element: <Products />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.view'],
  },
  {
    path: 'orders/crm',
    element: <CRM />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders-crm.view'],
    requireCRMEnabled: true, // Solo permitir acceso si CRM está habilitado
  },
  {
    path: 'orders/crm/view/:orderId',
    element: <ViewCRM />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders-crm.view'],
    requireCRMEnabled: true, // Solo permitir acceso si CRM está habilitado
  },
  {
    path: 'crm/management-hours',
    element: <ManagementHours />,
    teamTypes: ['tenant_3pl'],
    permissions: ['orders-crm.view'],
    requireCRMEnabled: true,
  },
  {
    path: 'incidents',
    element: <Incidents />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    // permissions: ['incidents.view'], // Comentado temporalmente
  },
  {
    path: 'configuration/incident-mappings',
    element: <Mappings />,
    teamTypes: ['tenant_3pl'],
    permissions: ['incidents.mapping'],
  },
  {
    path: 'configuration/warehouse-materials',
    element: <WarehouseMaterials />,
    teamTypes: ['tenant_3pl'],
    permissions: ['materials.manage'],
  },
  {
    path: 'invoices/eligible-extra-work',
    element: <EligibleExtraWork />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/eligible-wholesale-preparation',
    element: <EligibleWholesalePreparation />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/eligible-crm-management',
    element: <EligibleCrmManagement />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'products-without-stock',
    element: <ProductsWithoutStock />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['product-without-stock-report.view'],
  },
  {
    path: 'products/create',
    element: <ProductCreateEdit />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.create'],
  },
  {
    path: 'products/edit/:id',
    element: <ProductCreateEdit />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.edit'],
  },
  {
    path: 'orders/create',
    element: <CreateEditOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.create'],
  },
  {
    path: 'orders/edit/:id',
    element: <CreateEditOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.edit'],
  },
  {
    path: 'orders/view/:orderId',
    element: <ViewOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.view'],
  },
  {
    path: 'packs',
    element: <Packs />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['packs.view'],
  },
  {
    path: 'packaging',
    element: <Packaging />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'packing-stations',
    element: <PackingStations />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:clientId/packaging-recipes',
    element: <ClientPackagingRecipes />,
    teamTypes: ['tenant_3pl'],
    permissions: ['packaging-recipes.view'],
  },
  {
    path: 'clients/:clientId/packaging-recipes/:recipeId',
    element: <ClientPackagingRecipes />,
    teamTypes: ['tenant_3pl'],
    permissions: ['packaging-recipes.view'],
  },
  {
    path: 'refunds',
    element: <RefundsList />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'refunds/:id/detail',
    element: <RefundDetail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'refunds/:id/receive',
    element: <RefundReceiveEntry />,
    teamTypes: ['tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'refund-receptions',
    element: <RefundReceptionsList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'refund-receptions/:id/receive',
    element: <RefundReceptionsReceive />,
    teamTypes: ['tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'returned',
    element: <Returned />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'returned/:id/receive',
    element: <Receive />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'returned/:id/locate',
    element: <Locate />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'reused',
    element: <ReusedList />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'warehouse/productivity',
    element: <ProductivityPage />,
    teamTypes: ['tenant_3pl'],
    permissions: ['preparations.timings.view', 'returns.view'],
    requireAll: false,
  },
  {
    path: 'warehouse/dashboard',
    element: <ReusedDashboard />,
    teamTypes: ['tenant_3pl'],
    permissions: ['returns.view', 'entries.dashboard.view'],
    requireAll: false,
  },
  {
    path: 'reused/dashboard',
    element: <RedirectLegacyReusedDashboard />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'reused/:id/receive',
    element: <ReusedReceive />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'reused/identify-app',
    element: <ReusedIdentifyApp />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'locations',
    element: <Locations />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['locations.view'],
  },
  {
    path: 'merchandise-blocks',
    element: <MerchandiseBlocksShell />,
    teamTypes: ['tenant_3pl'],
    permissions: ['merchandise-blocks.manage'],
    children: [
      { index: true, element: <MerchandiseBlocksList /> },
      { path: 'history', element: <MerchandiseBlocksHistory /> },
    ],
  },
  {
    path: 'merchandise-blocks/:id',
    element: <MerchandiseBlockDetail />,
    teamTypes: ['tenant_3pl'],
    permissions: ['merchandise-blocks.manage'],
  },
  {
    path: 'stock-transfers/new',
    element: <StockTransferEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['stock-transfer.manage'],
  },
  {
    path: 'stock-transfers/:transferId/edit',
    element: <StockTransferEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['stock-transfer.manage'],
  },
  {
    path: 'warehouse/stock-movements',
    element: <WarehouseStockMovements />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['stock-movements.view'],
  },
  {
    path: 'carts',
    element: <Carts />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'pending-orders',
    element: <PendingOrders />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['orders.create-preparation'],
  },
  {
    path: 'held-for-preparation',
    element: <OrdersHeldForPreparation />,
    teamTypes: ['tenant_3pl'],
    permissions: ['orders.manage-held'],
  },
  {
    path: 'wallets',
    element: <Wallets />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['wallets.view'],
  },
  {
    path: 'wallets/:id',
    element: <Wallet />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['wallets.view'],
  },
  {
    path: 'wallets/withdrawals',
    element: <Withdrawals />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['withdrawals.view'],
  },
  {
    path: 'withdrawal-payment-files',
    element: <WithdrawalPaymentFiles />,
    teamTypes: ['tenant_3pl'],
    permissions: ['withdrawals.view'],
  },
  {
    path: 'wallets/bank-accounts',
    element: <BankAccounts />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['bank-accounts.view'],
  },
  {
    path: 'wallets/topups',
    element: <Topups />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['topups.view'],
  },
  {
    path: 'wholesale-preparations',
    element: <WholesalePreparations />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-orders',
    element: <WholesaleList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-orders/new',
    element: <WholesaleCreate />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['wholesale_orders.create'],
  },
  {
    path: 'wholesale-orders/:id',
    element: <WholesaleDetail />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-orders/:id/preparation',
    element: <WholesalePreparation />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-orders/:id/labels',
    element: <WholesaleLabels />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-pickups',
    element: <WholesalePickups />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'wholesale-pickups/:id',
    element: <WholesalePickups />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'locations/staging-zones',
    element: <WholesaleStagingZones />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },

  {
    path: 'configuration/wholesale-staging-zones',
    element: <Navigate to="/locations/staging-zones" replace />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wholesale_orders.view'],
  },
  {
    path: 'configuration/printers',
    element: <Printers />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['printers.view'],
  },
  {
    path: 'configuration/general',
    element: <General />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['configuration.view'],
  },
  {
    path: 'configuration/couriers',
    element: <IntegratedMessaging />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['shipping.couriers.view'],
  },
  {
    path: '/configuration/profile',
    element: <Profile />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: '/configuration/notifications',
    element: <NotificationSettings />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'stock-movements',
    element: <StockMovementsV2 />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['stock-movements.view'],
  },
  {
    path: 'location-movements',
    element: <LocationMovementsV2 />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['stock-movements.view'],
  },
  {
    path: 'reports/client-activity',
    element: <ClientActivityPage />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/client-activity/clients/:clientId/orders',
    element: <ClientActivityOrdersPage />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/logistics',
    element: <Logistics />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'reports/logistic-pivot',
    element: <Navigate to="/reports/logistics#logistic-pivot-dynamic" replace />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
  },
  {
    path: 'reports/profitability/orders/:orderId',
    element: <ProfitabilityOrderDetailPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/ad-spend',
    element: <AdSpendPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/shipping-costs',
    element: <DefaultShippingCostsPage />,
    teamTypes: ['tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/product-costs',
    element: <ProductCostsPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability',
    element: <Profitability />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'configuration/tracking-monitoring',
    element: <Tracking />,
    teamTypes: ['tenant_3pl'],
    permissions: ['tracking-monitoring.view'],
  },
  {
    path: 'configuration/tracking-files',
    element: <TrackingFiles />,
    teamTypes: ['tenant_3pl'],
    permissions: ['tracking-monitoring.view'],
  },
  {
    path: 'reports/inventory',
    element: <Inventory />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['inventory-report.view'],
  },
  {
    path: 'reports/marketing',
    element: <Marketing />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
  },
  {
    path: 'reports/orders-without-weight',
    element: <OrdersWithoutWeight />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/wallet-accounting-sync',
    element: <AccountingSyncList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wallets.view'],
    requireWalletVisible: true,
  },
  {
    path: 'reports/warehouse/ready-to-prepare-by-hour',
    element: <ReadyToPrepareByHour />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/warehouse/excel',
    element: <WarehouseExcelReports />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/3pl/excel',
    element: <ThreePlExcelReports />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/3pl/legacy-dashboard',
    element: <LegacyDashboard />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'accounting/dashboard',
    element: <AccountingDashboard />,
    teamTypes: ['tenant_3pl'],
    permissions: [
      'invoices.view',
      'cod-payments.payouts.view',
      'cod-payments.ledger.view',
      'withdrawals.view',
      'wallets.view',
    ],
  },
  {
    path: 'accounting/data-load',
    element: <DataLoad />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'reports/cod-payments',
    element: <Refunds />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['reports.view'],
  },
  {
    path: 'merchandise/*',
    element: <MerchandiseV3 />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['merchandises.view'],
  },
  {
    path: 'receptions',
    element: <Receptions />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'receptions/:id',
    element: <ReceptionDetailPage />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'cross-docking/operations/new',
    element: <CrossDockingOperationsCreate />,
    teamTypes: ['tenant_external'],
    permissions: ['cross-docking.operations.manage'],
  },
  {
    path: 'cross-docking/operations/edit/:operationId',
    element: <CrossDockingOperationsCreate />,
    teamTypes: ['tenant_external'],
    permissions: ['cross-docking.operations.manage'],
  },
  {
    path: 'cross-docking/operations/:operationId',
    element: <CrossDockingOperationsDetail />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cross-docking.operations.view'],
  },
  {
    path: 'cross-docking/operations',
    element: <CrossDockingOperationsList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cross-docking.operations.view'],
  },
  {
    path: 'cross-docking/receptions/:receptionId/receiving',
    element: <CrossDockingReceiving />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cross-docking.units.manage'],
  },
  {
    path: 'cross-docking/receptions/:receptionId/packing/:sessionId',
    element: <CrossDockingPacking />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cross-docking.packing.manage'],
  },
  {
    path: 'cross-docking/receptions/:receptionId',
    element: <CrossDockingReceptionWorkspace />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cross-docking.receptions.view'],
  },
  {
    path: 'orders/shipment-status-transitions',
    element: <ShipmentStatusTransitions />,
    teamTypes: ['tenant_3pl'],
    permissions: ['orders.shipment-status-transition.manage'],
  },
  {
    path: 'unreferenced-entries',
    element: <UnreferencedEntries />,
    teamTypes: ['tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'unreferenced-entries/:id',
    element: <UnreferencedEntryDetail />,
    teamTypes: ['tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'orders',
    element: <Orders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.view'],
  },
  {
    path: 'invoices/eligible-shipments',
    element: <InvoicesOrdersList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/eligible-storage',
    element: <EligibleStorage />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/shipment-credits',
    element: <ShipmentCredits />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wallet.shipment-credits.view'],
  },
  {
    path: 'invoices/minimum_fees',
    element: <MinimumFees />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/pre-invoices',
    element: <PreInvoices />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices',
    element: <InvoicesList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/view/:preInvoiceId',
    element: <InvoicesView />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/pre-invoices/view/:preInvoiceId',
    element: <ViewPreInvoices />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/rates',
    element: <RatesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.view'],
  },
  {
    path: 'invoices/rates/:id',
    element: <RateDetail />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.view'],
  },
  {
    path: 'invoices/rates/new',
    element: <CreateRate />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.manage'],
  },
  {
    path: 'orders-shipped',
    element: <OrdersShipped />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shipped-orders.view'],
  },
  {
    path: 'batches',
    element: <Batches />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['products.view'],
  },
  {
    path: 'batches/:id/*',
    element: <BatchDetail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['products.view'],
  },
  {
    path: 'extra-work-orders',
    element: <ExtraWorkOrdersList />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['extra-work-orders.view'],
  },
  {
    path: 'extra-work-orders/:id',
    element: <ExtraWorkOrderDetail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['extra-work-orders.view'],
  },
  {
    path: 'clients',
    element: <Clients />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/create',
    element: <CreateEditClient />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:id',
    element: <ViewClient />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:clientId/rules',
    element: <ClientRules />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:clientId/rules/create',
    element: <ClientRulesRedirectFromCreate />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.create'],
  },
  {
    path: 'clients/:clientId/rules/:id/edit',
    element: <ClientRulesRedirectFromEdit />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.edit'],
  },
  {
    path: 'clients/:clientId/rules/:id',
    element: <ClientRulesRedirectFromView />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'configuration/preparation-calendar',
    element: <PreparationCalendar />,
    teamTypes: ['tenant_3pl'],
    permissions: ['configuration.view'],
  },
  {
    path: 'configuration/roles',
    element: <Roles />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    requireRoleAdmin: true,
  },
  {
    path: 'configuration/users',
    element: <Users />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    requireRoleAdmin: true,
    roleAdminAlternativeAccess: { teamTypes: ['tenant_3pl'], permissions: ['iam.users.manage-operator-security'] },
  },
  {
    path: 'shops',
    element: <Shops />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shops.view'],
  },
  {
    path: 'shops/import-history/:shopId?',
    element: <ImportHistory />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shops.view'],
  },
  {
    // Retorno OAuth de Mercado Libre: solo existe en el deploy de México.
    path: 'shops/mercadolibre/callback',
    element: APP_CONFIG.isMexicoDeploy ? <MercadoLibreCallback /> : <Navigate to="/shops" replace />,
    teamTypes: ['tenant', 'tenant_external'],
    permissions: ['shops.create'],
  },
  {
    path: 'configuration/suscription',
    element: <Suscription />,
    teamTypes: ['tenant'],
    permissions: ['configuration.view'],
  },
  // Hotfix: afiliados ocultos (ver imports lazy).
  // {
  //   path: 'configuration/affiliates',
  //   element: <Affiliates />,
  //   teamTypes: ['tenant_external'],
  //   permissions: ['affiliates.me.view'],
  // },
  // {
  //   path: 'accounting/affiliate-partners',
  //   element: <AffiliatesListPage />,
  //   teamTypes: ['tenant_3pl'],
  //   permissions: ['affiliates.view'],
  // },
  // {
  //   path: 'accounting/affiliate-partners/:id',
  //   element: <AffiliateDetailPage />,
  //   teamTypes: ['tenant_3pl'],
  //   permissions: ['affiliates.view'],
  // },
  // {
  //   path: 'accounting/affiliate-commissions',
  //   element: <AffiliateCommissionsListPage />,
  //   teamTypes: ['tenant_3pl'],
  //   permissions: ['affiliates.commissions.backoffice.view'],
  // },
  {
    path: 'configuration/coverage-zones',
    element: <CoverageZones />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['shipping.coverage.view'],
  },
  {
    path: 'configuration/popups/*',
    element: <Popups />,
    teamTypes: ['tenant_3pl'],
    permissions: ['popups.view'],
  },
  {
    path: 'configuration/shipping-rule-presets/create',
    element: <ShippingRulePresetEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.create'],
  },
  {
    path: 'configuration/shipping-rule-presets/:presetId/edit',
    element: <ShippingRulePresetEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.edit'],
  },
  {
    path: 'configuration/shipping-rule-presets/history',
    element: <ShippingRulePresetsHistory />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.view'],
  },
  {
    path: 'configuration/shipping-rule-presets',
    element: <ShippingRulePresets />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.view'],
  },
  {
    path: 'configuration/shipment-status-mapping',
    element: <ShipmentStatusMapping />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipment_status_mappings.view'],
  },
  {
    path: 'configuration/product-categories',
    element: <ProductCategories />,
    teamTypes: ['tenant_3pl'],
    permissions: ['categories.view'],
  },
  {
    path: 'configuration/integrations',
    element: <Integrations />,
    teamTypes: ['tenant_external'],
    permissions: ['integrations.view'],
  },
  {
    path: 'integrations/zendesk/tickets',
    element: <CustomerSupportHub />,
    teamTypes: ['tenant_external'],
  },
  {
    path: 'integrations/zendesk/tickets/:ticketId',
    element: <TicketDetailView />,
    teamTypes: ['tenant_external'],
  },
  {
    path: 'configuration/webhooks',
    element: <Webhooks />,
    teamTypes: ['tenant_external'],
    permissions: ['webhooks.view'],
  },
  {
    path: 'configuration/webhooks/:id',
    element: <WebhookDeliveriesView />,
    teamTypes: ['tenant_external'],
    permissions: ['webhooks.view'],
  },
  {
    path: 'order-preparation',
    element: <OrderPreparation />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.view'],
  },
  {
    path: 'order-preparation/manifests',
    element: <Manifests />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.view'],
  },
  {
    path: 'order-preparation/picking/:id',
    element: <Picking />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.picking'],
  },
  {
    path: 'order-preparation/packing',
    element: <PackingByCart />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'order-preparation/packing/:id',
    element: <PackingV2 />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'order-preparation/massive-packing/:id',
    element: <MassivePacking />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'location-orders',
    element: <LocationOrders />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['location-orders.view'],
  },
  {
    path: 'location-orders/:id',
    element: <Detail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['location-orders.view'],
  },
  {
    path: 'relocations',
    element: <Relocations />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'relocations/new',
    element: <RelocationProcess />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'relocations/:id',
    element: <RelocationProcess />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'cod-payments/payouts',
    element: <PayoutsList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cod-payments.payouts.view'],
  },
  {
    path: 'cod-payments/payouts/:id',
    element: <PayoutView />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cod-payments.payouts.view'],
  },
  {
    path: 'cod-payments/payout-items',
    element: <PayoutItemsList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.payments.view'],
  },
  {
    path: 'cod-payments/courier-files',
    element: <CourierFilesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.files.view'],
  },
  {
    path: 'cod-payments/courier-files/:id',
    element: <CourierFileView />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.files.view'],
  },
  {
    path: 'cod-payments/mismatches',
    element: <MismatchesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.ledger.view'],
  },
  {
    path: 'cod-payments/reviews',
    element: <ReviewsList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.reviews.view'],
  },
  {
    path: 'verify-new-email',
    element: <VerifyNewEmailPage />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'warehouses',
    element: <WarehouseMapMain />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'warehouses/:id',
    element: <WarehouseMapView />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'logistics/sla/alerts',
    element: <SlaAlerts />,
    teamTypes: ['tenant_3pl'],
    permissions: ['sla.rules.view'],
  },
  {
    path: 'logistics/sla/claims',
    element: <SlaClaims />,
    teamTypes: ['tenant_3pl'],
    permissions: ['sla.rules.view'],
  },
  {
    path: 'logistics/sla/config/rules',
    element: <SlaRules />,
    teamTypes: ['tenant_3pl'],
    permissions: ['sla.rules.view'],
  },
  {
    path: 'logistics/sla/config/false-delivery',
    element: <SlaFalseDelivery />,
    teamTypes: ['tenant_3pl'],
    permissions: ['sla.rules.view'],
  },
  {
    path: 'logistics/sla/config/breach-types',
    element: <SlaBreachTypes />,
    teamTypes: ['tenant_3pl'],
    permissions: ['sla.rules.view'],
  },
  {
    path: 'logistics/couriers/tracking-failures',
    element: <TrackingFailures />,
    teamTypes: ['tenant_3pl'],
    permissions: ['tracking-monitoring.view'],
  },
  {
    path: 'analytical-accounting/cost-centers',
    element: <AnalyticalAccounting />,
    teamTypes: ['tenant_3pl'],
    permissions: ['analytical-accounting.cost-centers.manage'],
  },
];
